import React, { useState, useEffect, useContext } from 'react';
import { MyContext } from '../MainContext';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faPowerOff, faArrowLeft, faHeart } from '@fortawesome/free-solid-svg-icons';
import firebaseNotificationService from '../utils/firebaseNotificationService'; // Updated to use Firebase service
import NotificationPermission from './NotificationPermission'; // Import your modal component
import SupportClowzaModal from './SupportClowzaModal';
import { backendUrl } from '../utils/variables';

const Header = ({ showBackButton, onBackClick, backText }) => {
  const { user, exit_user } = useContext(MyContext);
  const navigate = useNavigate();
  const [notificationState, setNotificationState] = useState({
    loading: false, // For internal loading states if any direct action was here, but less relevant now
    subscribed: false, // Indicates if the user is currently subscribed to push notifications
    supported: firebaseNotificationService.supported, // Checks if browser supports notifications
    permission: typeof Notification !== 'undefined' ? Notification.permission : 'denied' // 'default', 'granted', 'denied'
  });
  const [showNotificationModal, setShowNotificationModal] = useState(false); // New state to control modal visibility
  const [showSupportModal, setShowSupportModal] = useState(false); // State to control support modal visibility
  const [showLogoutModal, setShowLogoutModal] = useState(false); // State to control logout confirmation modal visibility

  // Admin access is loaded via allowlist in MainContext (admin_emails.php)

  // Function to send FCM token to backend
  const sendFCMTokenToBackend = async (fcmToken) => {
    if (!user || !user.user_id || !fcmToken) {
      console.log('Cannot send FCM token: missing user data or token');
      return;
    }

    try {
      console.log('Sending FCM token to backend...');
      
      const response = await fetch(`${backendUrl}/save_fcm_token.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.user_id,
          email: user.email,
          fcm_token: fcmToken,
          device_type: 'web'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save FCM token');
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to save FCM token');
      }

      console.log('✅ FCM token saved successfully');
    } catch (error) {
      console.error('❌ Error saving FCM token:', error);
    }
  };

  // Function to refresh the notification status
  const refreshNotificationStatus = async () => {
    if (firebaseNotificationService.supported) {
      const currentPermission = typeof Notification !== 'undefined' ? Notification.permission : 'denied';
      setNotificationState(s => ({ ...s, permission: currentPermission }));

      // Only check subscription status if permission is granted
      if (currentPermission === 'granted') {
        try {
          // Initialize the service to ensure it's ready and get the token
          await firebaseNotificationService.initialize();
          const token = await firebaseNotificationService.getFCMToken(); // getFCMToken returns the current FCM token if subscribed
          console.log('FCM token obtained successfully via refreshNotificationStatus');
          
          // Send FCM token to backend if we have a token
          if (token) {
            await sendFCMTokenToBackend(token);
          }
          
          setNotificationState(s => ({ ...s, subscribed: !!token })); // subscribed is true if a token exists
        } catch (error) {
          console.error("Error refreshing notification token status:", error);
          setNotificationState(s => ({ ...s, subscribed: false }));
        }
      } else {
        setNotificationState(s => ({ ...s, subscribed: false }));
      }
    }
  };

  useEffect(() => {
    // Initial check when component mounts
    refreshNotificationStatus();

    // Listen for changes in permission status if needed (e.g., user changes settings outside app)
    // Note: Browser APIs for this are not standardized. Manual refresh might be needed or relying on re-render.
    // For simplicity, we'll rely on our modal to trigger refresh.
  }, []);

  const handleNotificationIconClick = () => {
    // Simply open the notification permission modal
    setShowNotificationModal(true);
  };

  const handleModalClose = () => {
    // When the modal closes, refresh the status to reflect any changes made in the modal
    setShowNotificationModal(false);
    refreshNotificationStatus();
  };

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    setShowLogoutModal(false);
    exit_user();
    navigate("/");
  };

  const cancelLogout = () => {
    setShowLogoutModal(false);
  };

  return (
    <>
      <nav className="navbar navbar-expand-lg navbar-light bg-light fixed-top">
        <div className="container">
          <div className="d-flex align-items-center">
            {showBackButton && (
              <button 
                className="btn btn-link text-decoration-none me-3"
                onClick={onBackClick}
                style={{ fontSize: '1.1rem', color: '#6c757d' }}
              >
                <FontAwesomeIcon icon={faArrowLeft} className="me-1" />
                {backText || 'Back'}
              </button>
            )}
            
            <a className="navbar-brand" onClick={() => navigate("/")}>
              <img 
                src={`${process.env.PUBLIC_URL}/logo_wordmark_color.svg`} 
                alt="Logo" 
                className="img-fluid hm-logo" 
                style={{maxWidth:'150px'}} 
              />
            </a>
          </div>

          <div className="ml-auto d-flex align-items-center">
            <button 
              className="btn btn-link text-decoration-none me-3"
              onClick={() => setShowSupportModal(true)}
              style={{ 
                fontSize: '1.1rem', 
                padding: '0',
                border: 'none'
              }}
              title="Support Clowza"
            >
              <FontAwesomeIcon 
                icon={faHeart} 
                className="breathing-heart-icon"
              />
            </button>
            {notificationState.supported && (
              <button 
                className="btn btn-link text-decoration-none me-3"
                onClick={handleNotificationIconClick} // Now just opens the modal
                disabled={notificationState.loading} // Keep for any internal loading (e.g., initial check)
                style={{ 
                  fontSize: '1.1rem', 
                  color: notificationState.subscribed ? '#3b6f64' : '#6c757d', // Active if subscribed
                  position: 'relative'
                }}
                title={notificationState.subscribed ? "Notifications Enabled" : "Manage Notifications"}
              >
                <FontAwesomeIcon 
                  icon={faBell} 
                  // No spin here, as the modal handles the loading UI
                />
                {/* Visual indicator for enabled notifications */}
                {notificationState.subscribed && (
                  <span className="notification-dot"></span>
                )}
              </button>
            )}
            <FontAwesomeIcon 
              icon={faPowerOff} 
              onClick={handleLogoutClick} 
              style={{ cursor: 'pointer' }}
            />
          </div>
        </div>
      </nav>

      {/* Render the NotificationPermission modal */}
      <NotificationPermission
        show={showNotificationModal}
        onHide={handleModalClose}
        onSuccess={handleModalClose} // If subscription is successful, close the modal and refresh status
      />

      {/* Render the SupportClowza modal */}
      <SupportClowzaModal
        show={showSupportModal}
        onHide={() => setShowSupportModal(false)}
      />

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div
          className="modal show"
          tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1050 }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Logout</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={cancelLogout}
                ></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to log out? You will need to sign in again to access your account.</p>
              </div>
              <div className="modal-footer">
                <button 
                  className="btn btn-secondary" 
                  onClick={cancelLogout}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-primary" 
                  onClick={confirmLogout}
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
